import { countryForVenue, quartileInstant } from '../utils/seatingTime';
import { changeableStates, states } from './reservation-states';

/**
 * Where a reservation is in its life, as far as the guest is concerned.
 *
 * - active:    still ahead of us; the guest may change/cancel it (subject to state rules)
 * - started:   the booked time has begun but the restaurant has not marked the guest seated
 * - seated:    the restaurant has seated the guest and the booked time is not over
 * - completed: the visit is over (DONE, NO_SHOW, or the booked time has ended)
 * - cancelled: cancelled by the guest or the restaurant
 * - declined:  a request the restaurant refused
 * - expired:   a waitlist entry or request whose time passed without becoming a booking
 *
 * Everything except `active` is locked: it can neither be changed nor cancelled.
 */
export type ReservationPhase =
  | 'active'
  | 'started'
  | 'seated'
  | 'completed'
  | 'cancelled'
  | 'declined'
  | 'expired';

const QUARTILE_MS = 15 * 60 * 1000;
// Used when a reservation has no usable end quartile.
const FALLBACK_DURATION_QUARTILES = 8;

type ReservationTiming = Pick<
  Reservation,
  'state' | 'dateString' | 'venueId' | 'startQuartile' | 'endQuartile' | 'waitlistQuartiles'
>;

/**
 * When a quartile of the reservation's business day starts, in the venue's timezone. Read from
 * dateString + quartile rather than seatingDate, which is wrong for Finland and for seatings
 * after midnight (see lib/utils/seatingTime.ts). Null when the reservation lacks the fields.
 */
function quartileMs(reservation: ReservationTiming, quartile: number): number | null {
  try {
    const country = countryForVenue({ venueId: reservation.venueId });
    return quartileInstant(reservation.dateString, quartile, country).getTime();
  } catch {
    return null;
  }
}

/**
 * The last moment the guest can still act on the reservation. For a waitlist entry that spans
 * several quartiles this is the latest wished-for start, since a table can still open up (and
 * be accepted, which cancels the waitlist entry) until then.
 */
export function getReservationCutoff(reservation: ReservationTiming): number | null {
  if (reservation.state === states.WAITLIST && reservation.waitlistQuartiles?.length) {
    const latest = Math.max(reservation.startQuartile ?? 0, ...reservation.waitlistQuartiles);
    return quartileMs(reservation, latest);
  }

  return quartileMs(reservation, reservation.startQuartile);
}

export function getReservationEnd(reservation: ReservationTiming): number | null {
  if (reservation.endQuartile > reservation.startQuartile) {
    return quartileMs(reservation, reservation.endQuartile);
  }

  const start = quartileMs(reservation, reservation.startQuartile);
  return start === null ? null : start + FALLBACK_DURATION_QUARTILES * QUARTILE_MS;
}

export function getReservationPhase(
  reservation: ReservationTiming | undefined | null,
  now: number = Date.now(),
): ReservationPhase | undefined {
  if (!reservation) return undefined;

  const { state } = reservation;

  if (state === states.CANCELLED || state === states.BLOCK) return 'cancelled';
  if (state === states.REFUSED) return 'declined';
  if (state === states.DONE || state === states.NO_SHOW) return 'completed';

  const end = getReservationEnd(reservation);
  const isOver = end !== null && now >= end;

  if (state === states.SEATED || state === states.PRESEATED) {
    return isOver ? 'completed' : 'seated';
  }

  const cutoff = getReservationCutoff(reservation);
  const hasStarted = cutoff !== null && now >= cutoff;

  if (state === states.WAITLIST || state === states.REQUESTED) {
    return hasStarted ? 'expired' : 'active';
  }

  if (isOver) return 'completed';
  if (hasStarted) return 'started';
  return 'active';
}

export function isReservationLocked(
  reservation: ReservationTiming | undefined | null,
  now: number = Date.now(),
) {
  const phase = getReservationPhase(reservation, now);
  return phase !== undefined && phase !== 'active';
}

export function canChangeReservation(
  reservation: ReservationTiming | undefined | null,
  now: number = Date.now(),
) {
  return (
    getReservationPhase(reservation, now) === 'active' &&
    changeableStates.includes(reservation.state)
  );
}

export function canCancelReservation(
  reservation: ReservationTiming | undefined | null,
  now: number = Date.now(),
) {
  return getReservationPhase(reservation, now) === 'active';
}

/** Error code the booking API routes return (HTTP 409) when a locked reservation is modified. */
export const RESERVATION_LOCKED = 'RESERVATION_LOCKED';
